import type { ICellRendererParams } from "ag-grid-community";

import {
  type ResolvedDateTimeFormat,
  useDateTimeFormat,
} from "@/shared/datetime";

import { isEmptyValue } from "../../cellValues";
import { EmptyCell } from "../EmptyCell";

import styles from "./TimestampCell.module.css";
import { useRelativeTick } from "./useRelativeTick";

export type TimestampVariant = "precise" | "relative";

/** Below this age, `relative` renders live "N minutes ago" text and ticks. */
const RELATIVE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

/**
 * The absolute (stacked date-over-time) portion of every variant is rendered
 * through the viewer's active date-format preference (see `@/shared/datetime`),
 * so it honors a preset/clock the user chose in Preferences and falls back to
 * the browser locale until that preference loads. Everything renders in the
 * viewer's local time zone with no inline zone label — since everything is
 * local the zone is constant; the tooltip carries the UTC instant.
 *
 * Relative phrasing ("3 minutes ago") is deliberately NOT run through the
 * preference: a date-ordering/clock preset says nothing about relative wording,
 * so it stays locale-driven via `Intl.RelativeTimeFormat`.
 */
const relativeFmt = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

type TimestampCellProps = ICellRendererParams & { variant?: TimestampVariant };

/**
 * The authoritative UTC instant for the tooltip, e.g.
 * "2025-03-18 16:54:37.123 UTC". Derived from the already-computed `iso` (no
 * second `toISOString`); the visible cell already shows the local time.
 */
function utcTooltip(iso: string): string {
  return iso.replace("T", " ").replace("Z", " UTC");
}

/**
 * Locale-aware relative phrasing. `Intl.RelativeTimeFormat` localizes a
 * (value, unit) pair — it does not pick the unit, so this only buckets into
 * "now" / minutes / hours. Floored to minutes, matching the 60s tick.
 */
function formatRelative(instant: Date): string {
  const mins = Math.round((instant.getTime() - Date.now()) / 60_000); // < 0 = past
  if (mins > -1) {
    return relativeFmt.format(0, "second"); // < ~60s ago → localized "now"
  }
  if (mins > -60) {
    return relativeFmt.format(mins, "minute");
  }
  return relativeFmt.format(Math.round(mins / 60), "hour");
}

/**
 * Stacked date over time, wrapped in a `<time>` carrying the exact instant.
 * The visible date and time both come from the active format preference at the
 * requested `precision`.
 */
function StackedTimestamp({
  format,
  instant,
  iso,
  title,
  precision,
}: {
  format: ResolvedDateTimeFormat;
  instant: Date;
  iso: string;
  title: string;
  precision: "minutes" | "seconds";
}) {
  const { date, time } = format.formatParts(instant, { time: precision });
  return (
    <time dateTime={iso} title={title} className={styles.stack}>
      <span className={styles.date}>{date}</span>
      <span className={styles.time}>{time}</span>
    </time>
  );
}

/**
 * Adaptive `relative` renderer: under 24h it shows live relative text and
 * subscribes to the shared ticker; at/after 24h it shows the trimmed stacked
 * form and never subscribes. On the tick that crosses 24h, `recent` flips, the
 * cell re-renders into the static form, and the effect cleanup unsubscribes it.
 */
function RelativeTimestamp({
  format,
  instant,
  iso,
  title,
}: {
  format: ResolvedDateTimeFormat;
  instant: Date;
  iso: string;
  title: string;
}) {
  const recent = Date.now() - instant.getTime() < RELATIVE_THRESHOLD_MS;
  useRelativeTick(recent);

  if (!recent) {
    return (
      <StackedTimestamp
        format={format}
        instant={instant}
        iso={iso}
        title={title}
        precision="minutes"
      />
    );
  }
  return (
    <time dateTime={iso} title={title} className={styles.inline}>
      {formatRelative(instant)}
    </time>
  );
}

/**
 * AG Grid `cellRenderer` for timestamp columns, rendered in the viewer's local
 * time zone and their active date-format preference (see `@/shared/datetime`).
 *
 * Variants:
 *  - `precise` (default): stacked date over full time (with seconds), for
 *    detail-critical timestamps such as event boundaries.
 *  - `relative`: under 24h, live "N minutes ago"; at/after 24h, stacked date
 *    over trimmed time (no seconds). For created/modified timestamps.
 */
export function TimestampCell({
  value,
  variant = "precise",
}: TimestampCellProps) {
  // Subscribes to the active preference so every cell re-renders when the user
  // changes it. Called unconditionally (before the early returns below) to
  // satisfy the rules of hooks.
  const format = useDateTimeFormat();

  if (isEmptyValue(value)) {
    return <EmptyCell />;
  }
  const instant = new Date(value as string | number | Date);
  if (Number.isNaN(instant.getTime())) {
    return <EmptyCell />;
  }
  const iso = instant.toISOString();
  const title = utcTooltip(iso);

  if (variant === "relative") {
    return (
      <RelativeTimestamp
        format={format}
        instant={instant}
        iso={iso}
        title={title}
      />
    );
  }
  return (
    <StackedTimestamp
      format={format}
      instant={instant}
      iso={iso}
      title={title}
      precision="seconds"
    />
  );
}
